//Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

//3rd party imports
import * as CryptoJS from 'crypto-js';
import { JwtHelperService } from '@auth0/angular-jwt';
import { plainToInstance, serialize, deserialize } from 'class-transformer';

//Rxjs import,
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

//Service imports
import { RPCacheRegistrationService } from './shared-services/rp-cache-registration-service';

//Model imports
import { LoginParameterDto } from '../_models/login-parameter-dto';
import { PasswordUpdateDto } from '../_models/password-update-dto';
import { UserInfoResultDto } from '../_models/user-info-result-dto';
import { BrandingConfigurationResultDto } from '../_models/branding-configuration-result-dto';
import { GoogleRegistrationResponseDto } from '../_models/google-registration-response-dto';
import { LinkedAccountInfoDto } from '../_models/linked-account-info-dto';

//Constant imports
import { StorageKeyConstants } from '../_constants/storage-key-constants';
import { CacheDuration } from '../_constants/cache-duration';
import { STORAGE_PREFIX as FEATURE_FLAG_PREFIX } from '../_constants/feature-flags';

//Service imports
import { DatadogService } from './datadog.service';
import { EnvironmentConfigService } from './environment-config.service';
import { MeetingHubWidgetService } from '../shared-components/meeting-hub-widget/meeting-hub-widget.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  jwtHelper = new JwtHelperService();

  decodedToken: any;
  private _subjectLoginSuccess: Subject<boolean> = new Subject(); //to notify other component that users have logged in successfully.
  private _subjectProfileImageUpdated: Subject<string> = new Subject(); //notify when profile image is updated successfully
  private _subjectLogout: Subject<boolean> = new Subject(); //notify when logout is clicked
  private readonly _secretKey = '126af219-9db5-4168-ac54-0ba7ff860d5f';

  constructor(
    private http: HttpClient,
    private router: Router,
    private cacheRegistrationService: RPCacheRegistrationService,
    private datadogService: DatadogService,
    private envConfig: EnvironmentConfigService,
    private meetingHubWidgetService: MeetingHubWidgetService
  ) {}

  getSubjectLoginSuccess(): Subject<boolean> {
    return this._subjectLoginSuccess;
  }

  updateSubjectLoginSuccess(value: boolean): void {
    this._subjectLoginSuccess.next(value);
  }

  getSubjectLogout(): Subject<boolean> {
    return this._subjectLogout;
  }

  updateSubjectLogout(value: boolean): void {
    this._subjectLogout.next(value);
  }

  getSubjectProfileImageUpdated(): Subject<string> {
    return this._subjectProfileImageUpdated;
  }

  updateSubjectProfileImageUpdated(value: string): void {
    this._subjectProfileImageUpdated.next(value);
  }

  logOut(queryParamsValue: string = null) {
    this.clearAuthLocalStorageData();
    this.cacheRegistrationService.deleteAllCache();
    this.updateSubjectLoginSuccess(false);
    this.decodedToken = null;

    // Clear user from Datadog
    this.datadogService.clearUser();

    if (queryParamsValue == null) {
      this.router.navigate(['user', 'login']);
    } else {
      this.router.navigate(['user', 'login'], {
        queryParams: { returnUrl: queryParamsValue }
      });
    }
  }

  get isLoggedIn(): boolean {
    let loggedIn = false;
    const token = localStorage.getItem(StorageKeyConstants.apiTokenName);
    if (!!token) {
      loggedIn = !this.jwtHelper.isTokenExpired(token);
    }
    return loggedIn;
  }

  // Design decision: returns the login (impersonation) token first.
  // During impersonation, apiLoginTokenName holds the admin's credential.
  // Callers that need the current effective token (e.g. SparkpostService) receive
  // the admin token so the suppression proxy can enforce admin-only operations
  // server-side. This diverges intentionally from JwtModule's ccTokenGetter, which
  // reads only apiTokenName (the resident token) and is used for standard API calls.
  // If the two ever need to converge, update ccTokenGetter in app.module.ts instead
  // of changing this method, to avoid breaking the impersonation flow.
  getAccessToken(): string | null {
    return (
      localStorage.getItem(StorageKeyConstants.apiLoginTokenName) ||
      localStorage.getItem(StorageKeyConstants.apiTokenName)
    );
  }

  getCurrentUserInfo(): any {
    const userInfoJson = localStorage.getItem(StorageKeyConstants.userInfo);
    if (userInfoJson) {
      return JSON.parse(userInfoJson);
    }
    return null;
  }

  login(loginParameterDto: LoginParameterDto): Observable<UserInfoResultDto> {
    return this.http
      .post<UserInfoResultDto>(
        this.envConfig.ccAuthenticationApiUrl + 'login',
        loginParameterDto
      )
      .pipe(
        map((response: any) => {
          const user = plainToInstance(UserInfoResultDto, response);
          if (user) {
            localStorage.setItem(StorageKeyConstants.apiTokenName, user.token);
            this.decodedToken = this.jwtHelper.decodeToken(user.token);
            localStorage.setItem(
              StorageKeyConstants.userInfo,
              JSON.stringify(user)
            );

            // Track user in Datadog
            this.datadogService.setUser(user);
          }
          this.clearRemembered();
          if (loginParameterDto.rememberMe === true) {
            this.rememberLogin(loginParameterDto);
          }
          return user;
        })
      );
  }

  updatePassword(data: PasswordUpdateDto) {
    return this.http.put(
      this.envConfig.ccAuthenticationApiUrl + `updatePassword`,
      data
    );
  }

  clearAuthLocalStorageData(): void {
    localStorage.removeItem(StorageKeyConstants.apiTokenName);
    localStorage.removeItem(StorageKeyConstants.apiLoginTokenName);
    localStorage.removeItem(StorageKeyConstants.userInfo);
    localStorage.removeItem(StorageKeyConstants.loggedInUserInfo);
    localStorage.removeItem(StorageKeyConstants.selectedPropertyKey);
    // Stale MH-JWTs carry baked-in claims (roles/tier/community access). If the
    // token outlives logout, MeetingHubWidgetService.hasToken() returns true on
    // next login and skips re-auth — the next session reuses the previous user's
    // claims and gets 403s after any backend authorization fix. RES-596.
    localStorage.removeItem(StorageKeyConstants.meetingHubTokenName);
    // RES-638: also drop the in-memory Meeting Hub cache (join URLs / passcodes /
    // dial-in) so a later session never serves the previous user's meeting data.
    this.meetingHubWidgetService.clearCache();
    this.clearFeatureFlagSessionStorage();
  }

  // URL-query feature flags (see _constants/feature-flags.ts) persist in
  // sessionStorage under the `ff_` prefix. Clear them on logout so a flag set
  // by one user does not leak into the next user's session in the same tab.
  // Wrapped in try/catch because `sessionStorage` access throws `SecurityError`
  // in private-mode browsers and certain restricted iframe contexts —
  // flag-state cleanup failing is non-fatal; logout shouldn't be.
  private clearFeatureFlagSessionStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(FEATURE_FLAG_PREFIX)) keysToRemove.push(key);
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {
      // Intentionally swallowed — see comment above.
    }
  }

  getBrandingInformation(
    communityId: number,
    clearCache: boolean = false
  ): Observable<BrandingConfigurationResultDto> {
    const url = `${this.envConfig.ccAuthenticationApiUrl.replace('/auth/', '/')}branding/config?CommunityId=${communityId}`;
    this.cacheRegistrationService.registerForCache(url, clearCache);
    const headers = this.cacheRegistrationService.getHttpRequestHeaders(
      url,
      CacheDuration.fifteenMinute
    );
    const options = { headers: headers };
    return this.http
      .get<BrandingConfigurationResultDto>(url, options)
      .pipe(
        map((resposeData) =>
          plainToInstance(BrandingConfigurationResultDto, resposeData)
        )
      );
  }

  getBrandingImageData(imageUrl: string): Observable<Blob> {
    return this.http
      .get(imageUrl, { responseType: 'arraybuffer' })
      .pipe(map((responseData: any) => new Blob([responseData])));
  }

  rememberLogin(loginParam: LoginParameterDto): void {
    const encrypted = this.encrypt(serialize(loginParam));
    localStorage.setItem(StorageKeyConstants.rememberedLoginKey, encrypted);
  }

  getRemembered(): LoginParameterDto {
    let loginParam = new LoginParameterDto();
    const remembered = localStorage.getItem(
      StorageKeyConstants.rememberedLoginKey
    );
    if (!!remembered) {
      const decrypted = this.decrypt(remembered);
      loginParam = deserialize(LoginParameterDto, decrypted);
    }
    return loginParam;
  }

  clearRemembered(): void {
    localStorage.removeItem(StorageKeyConstants.rememberedLoginKey);
  }

  encrypt(text: string): string {
    const ciphertext = CryptoJS.AES.encrypt(text, this._secretKey);
    return ciphertext.toString();
  }

  decrypt(ciphertext: string): string {
    const bytes: any = CryptoJS.AES.decrypt(ciphertext, this._secretKey);
    const plaintext = bytes.toString(CryptoJS.enc.Utf8);
    return plaintext;
  }

  exchangeGoogleCode(code: string): Observable<UserInfoResultDto> {
    const portalType = 'resident';
    return this.http
      .post<UserInfoResultDto>(
        `${this.envConfig.ccAuthenticationApiUrl}google-exchange`,
        { code, portalType }
      )
      .pipe(
        map((response: any) => {
          const user = plainToInstance(UserInfoResultDto, response);
          if (user) {
            localStorage.setItem(StorageKeyConstants.apiTokenName, user.token);
            this.decodedToken = this.jwtHelper.decodeToken(user.token);
            localStorage.setItem(
              StorageKeyConstants.userInfo,
              JSON.stringify(user)
            );

            // Track user in Datadog
            this.datadogService.setUser(user);
          }
          return user;
        })
      );
  }

  getGoogleRegistrationInfo(
    code: string
  ): Observable<GoogleRegistrationResponseDto> {
    return this.http
      .get<GoogleRegistrationResponseDto>(
        `${this.envConfig.ccAuthenticationApiUrl}google-register-exchange?code=${code}`
      )
      .pipe(
        map((response) =>
          plainToInstance(GoogleRegistrationResponseDto, response)
        )
      );
  }

  /**
   * Gets the Google OAuth URL for linking a Google account to the current user.
   * @param successUrl The URL path to redirect to after successful linking
   * @param errorUrl The URL path to redirect to after failed linking
   * @returns Observable with the redirect URL to Google OAuth
   */
  getLinkGoogleAccountUrl(
    successUrl: string,
    errorUrl: string
  ): Observable<{ redirectUrl: string }> {
    const spaOrigin = encodeURIComponent(window.location.origin);
    return this.http.post<{ redirectUrl: string }>(
      `${this.envConfig.ccAuthenticationApiUrl}link-google-account?spaOrigin=${spaOrigin}`,
      { successUrl, errorUrl }
    );
  }

  /**
   * Gets the linked account information for the current user.
   * @returns Observable with linked account details
   */
  getLinkedAccounts(): Observable<LinkedAccountInfoDto> {
    return this.http.get<LinkedAccountInfoDto>(
      `${this.envConfig.ccAuthenticationApiUrl}linked-accounts`
    );
  }

  /**
   * Unlinks the Google account from the current user.
   * @returns Observable indicating success
   */
  unlinkGoogleAccount(): Observable<void> {
    return this.http.delete<void>(
      `${this.envConfig.ccAuthenticationApiUrl}unlink-google-account`
    );
  }
}
